var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import { useMe } from '@/atoms/Auth';
import { placeFormV2GuideDismissedAtom, placeFormV2GuideDismissedUntilAtom, } from '@/atoms/User';
import { ScreenLayout } from '@/components/ScreenLayout';
import { UploadProgressOverlay } from '@/components/UploadProgressOverlay';
import { showUploadFailedToast, useImageUploadWithProgress, } from '@/hooks/useImageUploadWithProgress';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import { FloorMovingMethodTypeDto, PlaceDoorDirectionTypeDto, StairInfo, } from '@/generated-sources/openapi';
import useAppComponents from '@/hooks/useAppComponents';
import { useFormExitConfirm } from '@/hooks/useFormExitConfirm';
import { REGISTER_TIMEOUT_MS, useIdempotencyKey, } from '@/hooks/useIdempotencyKey';
import usePost from '@/hooks/usePost';
import Logger from '@/logging/Logger';
import { LogParamsProvider } from '@/logging/LogParamsProvider';
import FormExitConfirmBottomSheet from '@/modals/FormExitConfirmBottomSheet';
import { getCtplConquest } from '@/utils/ctplConquest';
import { invalidateChallengeQueries } from '@/utils/challengeQueryCache';
import { updateSearchCacheForPlaceAsync } from '@/utils/SearchPlacesUtils';
import ToastUtils from '@/utils/ToastUtils';
import { useBackHandler } from '@react-native-community/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useSetAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components/native';
import { pushItemsAtom } from '../SearchScreen/atoms/quest';
import FloorMovementStep from './components/FloorMovementStep';
import FloorStep from './components/FloorStep';
import GuideModal from './components/GuideModal';
import InfoStep from './components/InfoStep';
import { GUIDE_CONTENTS } from './constants';
import { getFloorConditions, computeFloors } from './hooks';
const STEPS = ['floor', 'info', 'floorMovement'];
export default function PlaceFormV2Screen({ route, navigation, }) {
    const { place, building } = route.params;
    const { api } = useAppComponents();
    const queryClient = useQueryClient();
    const pushItems = useSetAtom(pushItemsAtom);
    const { syncUserInfo } = useMe();
    const { uploadImages, uploadProgress } = useImageUploadWithProgress();
    const getIdempotencyKey = useIdempotencyKey();
    const [stepIndex, setStepIndex] = useState(0);
    // 뒤로가기 confirm
    const formExitConfirm = useFormExitConfirm(action => {
        navigation.dispatch(action);
    });
    const form = useForm();
    const step = STEPS[stepIndex];
    // form.watch()로 값 읽기
    const selectedOption = form.watch('floorOption');
    const selectedStandaloneType = form.watch('standaloneType');
    const selectedFloor = form.watch('selectedFloor');
    const doorDirection = form.watch('doorDirection');
    // Guide 모달 표시 여부
    const [isGuideModalVisible, setIsGuideModalVisible] = useState(false);
    // Guide 모달 "다시보지않기" 상태 (읽기/쓰기)
    const [guideDismissed, setGuideDismissed] = useAtom(placeFormV2GuideDismissedAtom);
    // Guide 모달 "확인했어요" 상태 (1일간 안 보기 - timestamp)
    const [guideDismissedUntil, setGuideDismissedUntil] = useAtom(placeFormV2GuideDismissedUntilAtom);
    // 현재 선택에 따른 Guide 모달 키 결정
    const guideKey = (() => {
        if (selectedOption === 'standalone') {
            if (selectedStandaloneType === 'singleFloor') {
                return 'standaloneSingleFloor';
            }
            if (selectedStandaloneType === 'multipleFloors') {
                return 'standaloneMultipleFloors';
            }
            return null;
        }
        return selectedOption;
    })();
    // Guide 모달 표시 여부 확인
    const shouldShowGuide = (() => {
        if (!guideKey)
            return false;
        // 영구적으로 차단된 경우
        const isPermanentlyDismissed = (() => {
            switch (guideKey) {
                case 'firstFloor':
                    return guideDismissed.firstFloor;
                case 'otherFloor':
                    return guideDismissed.otherFloor;
                case 'multipleFloors':
                    return guideDismissed.multipleFloors;
                case 'standaloneSingleFloor':
                    return guideDismissed.standaloneSingleFloor;
                case 'standaloneMultipleFloors':
                    return guideDismissed.standaloneMultipleFloors;
                default:
                    return false;
            }
        })();
        if (isPermanentlyDismissed)
            return false;
        // 1일간 안 보기 체크
        const dismissedUntilTimestamp = (() => {
            switch (guideKey) {
                case 'firstFloor':
                    return guideDismissedUntil.firstFloor;
                case 'otherFloor':
                    return guideDismissedUntil.otherFloor;
                case 'multipleFloors':
                    return guideDismissedUntil.multipleFloors;
                case 'standaloneSingleFloor':
                    return guideDismissedUntil.standaloneSingleFloor;
                case 'standaloneMultipleFloors':
                    return guideDismissedUntil.standaloneMultipleFloors;
                default:
                    return null;
            }
        })();
        if (dismissedUntilTimestamp && Date.now() < dismissedUntilTimestamp) {
            return false;
        }
        return true;
    })();
    // guideKey에 따른 가이드 콘텐츠 가져오기
    const guideContent = useMemo(() => {
        if (!guideKey)
            return GUIDE_CONTENTS.firstFloor;
        return GUIDE_CONTENTS[guideKey] || GUIDE_CONTENTS.firstFloor;
    }, [guideKey]);
    // 단독건물 선택 후 다른 옵션으로 변경하면 단독건물 타입 초기화
    useEffect(() => {
        if (selectedOption !== 'standalone') {
            form.setValue('standaloneType', null);
        }
    }, [selectedOption, form]);
    // "아니요, 다른층이에요" 선택 후 다른 옵션으로 변경하면 층 정보 초기화
    useEffect(() => {
        if (selectedOption !== 'otherFloor') {
            form.setValue('selectedFloor', undefined);
        }
    }, [selectedOption, form]);
    const submitMutation = usePost(['PlaceFormV2', 'Submit'], (_a) => __awaiter(this, [_a], void 0, function* ({ values, uploaded, requestId, }) {
        return submitRegistration(api, queryClient, place.id, values, selectedOption, selectedStandaloneType, selectedFloor, doorDirection, uploaded, requestId);
    }));
    const handleSubmit = () => __awaiter(this, void 0, void 0, function* () {
        if (submitMutation.isPending) {
            return;
        }
        const values = form.getValues();
        // 사진 URL 은 업로드마다 달라지므로 업로드 전 입력(로컬 사진 포함)으로 키를 정한다.
        const requestId = getIdempotencyKey({
            values,
            selectedOption,
            selectedStandaloneType,
            selectedFloor,
            doorDirection,
        });
        let uploaded;
        try {
            uploaded = yield uploadAllPhotos(api, values, selectedOption, selectedStandaloneType, uploadImages);
        }
        catch (e) {
            showUploadFailedToast(e);
            return;
        }
        const registered = yield submitMutation.mutateAsync({
            values,
            uploaded,
            requestId,
        });
        if (!registered.success) {
            return;
        }
        // PA 등록 성공 직후 앨범 업로드 권한(isAlbumUploadAllowed) 갱신 — 다음 카메라
        // 진입 전에 반영되도록 fire-and-forget으로 호출한다.
        syncUserInfo();
        // 정복 완료 축하 조건은 둘 다 서버의 사실이다.
        // ① 참여 중인 CTPL 챌린지의 대상 장소에 기여했는가
        //    (getCtplConquest 가 보는 conquerTargetPlaceList — 서버가 참여·소속을 판정)
        // ② 이번 등록이 그 장소의 첫 정복인가 (isFirstPlaceAccessibility)
        // 서버에 재등록 가드가 없어 ② 가 필요하다. 예전에는 PDP 의 react-query 캐시
        // 유무로 ② 를 추정했는데, PDP 를 거치지 않는 진입점(SearchItemCard →
        // PlaceFormV2)에서는 캐시가 없어 CTPL 주 동선 전체에서 축하가 억제됐다.
        const conquest = registered.isFirstPlaceAccessibility
            ? getCtplConquest(registered.contributedChallengeInfos)
            : undefined;
        // 정복 완료 축하가 뜨는 경우 기존 퀘스트 완료 스탬프 팝업은 억제한다 —
        // pushItems 를 호출하지 않으면 큐가 비어 QuestCompletionModal 렌더 조건
        // 자체가 성립하지 않는다.
        if (registered.data && !conquest) {
            pushItems(registered.data);
        }
        // Navigate to completion screen
        const event = getEvent(selectedOption, doorDirection);
        navigation.navigate('RegistrationComplete', {
            target: 'place',
            event,
            placeInfo: {
                place,
                building,
            },
            conquest,
        });
    });
    // 다음 단계로 이동
    const handleNext = () => {
        if (step === 'floor') {
            // floor에서 다음으로: guide가 필요하면 모달 표시
            if (shouldShowGuide) {
                setIsGuideModalVisible(true);
                return;
            }
            // floor에서는 항상 info로
            const infoIndex = STEPS.indexOf('info');
            if (infoIndex !== -1) {
                setStepIndex(infoIndex);
            }
        }
        else {
            // 다른 step에서는 순차적으로 다음으로
            setStepIndex(prev => Math.min(prev + 1, STEPS.length - 1));
        }
    };
    // 이전 단계로 이동
    const handleBack = () => {
        // guide 모달이 열려있으면 모달 닫기
        if (isGuideModalVisible) {
            setIsGuideModalVisible(false);
            return;
        }
        // 입력한 정보를 유지한 채로 이전 단계로 이동
        setStepIndex(prev => Math.max(prev - 1, 0));
    };
    // InfoStep에서 다음 버튼 핸들러
    const handleInfoSubmit = () => {
        // floorMovement 단계가 필요한지 확인
        const { showFloorMovement } = getFloorConditions({
            floorOption: selectedOption,
            standaloneType: selectedStandaloneType,
        });
        if (showFloorMovement) {
            const floorMovementIndex = STEPS.indexOf('floorMovement');
            if (floorMovementIndex !== -1) {
                setStepIndex(floorMovementIndex);
            }
        }
        else {
            // floorMovement가 필요없으면 바로 제출
            handleSubmit();
        }
    };
    // "다시보지 않기" 버튼 핸들러
    const handleDismissPermanently = () => {
        if (!guideKey)
            return;
        setGuideDismissed(prev => {
            switch (guideKey) {
                case 'firstFloor':
                    return Object.assign(Object.assign({}, prev), { firstFloor: true });
                case 'otherFloor':
                    return Object.assign(Object.assign({}, prev), { otherFloor: true });
                case 'multipleFloors':
                    return Object.assign(Object.assign({}, prev), { multipleFloors: true });
                case 'standaloneSingleFloor':
                    return Object.assign(Object.assign({}, prev), { standaloneSingleFloor: true });
                case 'standaloneMultipleFloors':
                    return Object.assign(Object.assign({}, prev), { standaloneMultipleFloors: true });
                default:
                    return prev;
            }
        });
        // 모달 닫고 info로 이동
        setIsGuideModalVisible(false);
        const infoIndex = STEPS.indexOf('info');
        if (infoIndex !== -1) {
            setStepIndex(infoIndex);
        }
    };
    // "가이드 보기 >" 링크 핸들러: shouldShowGuide 체크 없이 무조건 모달 표시
    const handleOpenGuide = () => {
        setIsGuideModalVisible(true);
    };
    // "확인했어요!" 버튼 핸들러: 1일간 안 보기
    const handleConfirmGuide = () => {
        if (guideKey) {
            const oneDayFromNow = Date.now() + 24 * 60 * 60 * 1000;
            setGuideDismissedUntil(prev => (Object.assign(Object.assign({}, prev), { [guideKey]: oneDayFromNow })));
        }
        // 모달 닫고 info로 이동
        setIsGuideModalVisible(false);
        const infoIndex = STEPS.indexOf('info');
        if (infoIndex !== -1) {
            setStepIndex(infoIndex);
        }
    };
    // 안드로이드 백 버튼 처리
    useBackHandler(() => {
        // guide 모달이 열려있으면 모달 닫기
        if (isGuideModalVisible) {
            handleBack();
            return true;
        }
        // 다른 step에서도 이전 step이 있으면 뒤로가기
        if (stepIndex > 0) {
            handleBack();
            return true;
        }
        // 첫 step에서는 기본 back 동작 허용 → usePreventRemove가 종료 확인 다이얼로그 표시
        return false;
    });
    const stepConfig = {
        floor: _jsx(FloorStep, { place: place, onNext: handleNext }),
        info: (_jsx(InfoStep, { place: place, isStandaloneBuilding: selectedOption === 'standalone', hasFloorMovementStep: getFloorConditions({
                floorOption: selectedOption,
                standaloneType: selectedStandaloneType,
            }).showFloorMovement, onSubmit: handleInfoSubmit, onBack: handleBack, onGuidePress: handleOpenGuide })),
        floorMovement: (_jsx(FloorMovementStep, { place: place, isStandaloneBuilding: selectedOption === 'standalone', onSubmit: handleSubmit, onBack: handleBack, onGuidePress: handleOpenGuide })),
    };
    return (_jsx(LogParamsProvider, { params: { place_id: place.id }, children: _jsxs(FormProvider, Object.assign({}, form, { children: [_jsxs(ScreenLayout, { isHeaderVisible: true, children: [_jsx(HeaderBorder, {}), stepConfig[step]] }), _jsx(GuideModal, { visible: isGuideModalVisible, guideContent: guideContent, onDismissPermanently: handleDismissPermanently, onConfirm: handleConfirmGuide, onRequestClose: handleBack }), _jsx(FormExitConfirmBottomSheet, { isVisible: formExitConfirm.isVisible, onConfirm: formExitConfirm.onConfirm, onCancel: formExitConfirm.onCancel }), _jsx(UploadProgressOverlay, Object.assign({}, uploadProgress))] })) }));
}
export const HeaderBorder = styled.View({
    borderBottomWidth: 1,
    borderBottomColor: color.blue5,
});
export const SectionSeparator = styled.View({
    backgroundColor: color.gray10,
    height: 6,
});
// Common styled components shared across PlaceFormV2 and BuildingFormV2
export const SectionLabel = styled.Text({
    fontSize: 14,
    lineHeight: 20,
    fontFamily: font.pretendardBold,
    color: color.brand50,
    letterSpacing: -0.28,
});
export const QuestionSection = styled.View({
    gap: 8,
});
export const QuestionText = styled.Text({
    fontSize: 18,
    lineHeight: 26,
    fontFamily: font.pretendardSemibold,
    color: color.gray80,
    letterSpacing: -0.4,
});
export const SubSection = styled.View({
    gap: 20,
});
export const Label = styled.Text({
    fontSize: 18,
    lineHeight: 26,
    fontFamily: font.pretendardSemibold,
    color: color.gray80,
    letterSpacing: -0.4,
});
export const RequiredMark = styled.Text({
    color: 'red',
    fontSize: 18,
    lineHeight: 26,
    letterSpacing: -0.36,
});
export const MeasureGuide = styled.View({
    aspectRatio: '315/152',
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: color.gray20,
});
export const GuideButton = styled.View ``;
export const GuideText = styled.Text `
  color: ${color.brandColor};
  font-size: 14px;
  font-family: ${font.pretendardMedium};
  text-align: right;
  letter-spacing: -0.28px;
`;
export const SubmitButtonWrapper = styled.View `
  background-color: ${color.white};
  padding-vertical: 12px;
  padding-horizontal: 20px;
  border-top-width: 1px;
  border-top-color: ${color.gray15};
  flex-direction: row;
  gap: 8px;
`;
export const OptionsGroup = styled.View `
  gap: 8px;
`;
export const Hint = styled.Text `
  font-size: 14px;
  line-height: 20px;
  font-family: ${font.pretendardRegular};
  color: ${color.gray45};
  letter-spacing: -0.28px;
`;
export const DoorDirectionContainer = styled.View `
  flex-direction: row;
  gap: 8px;
`;
export const DoorDirectionOption = styled.View `
  flex: 1;
  gap: 8px;
`;
export const DoorDirectionImageContainer = styled.View `
  width: 100%;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  border-width: 1px;
  border-color: ${color.gray20};
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
`;
function uploadAllPhotos(api, values, selectedOption, selectedStandaloneType, uploadImages) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const startImageUpload = Date.now();
        const entranceUrls = yield uploadImages(api, values.entrancePhotos, undefined, '입구 사진');
        const hasMultipleFloors = getFloorConditions({
            floorOption: selectedOption,
            standaloneType: selectedStandaloneType,
        }).showFloorMovement;
        let elevatorUrls;
        if (hasMultipleFloors &&
            ((_a = values.floorMovementMethod) === null || _a === void 0 ? void 0 : _a.includes(FloorMovingMethodTypeDto.PlaceElevator))) {
            elevatorUrls = yield uploadImages(api, values.elevatorPhotos || [], undefined, '엘리베이터 사진');
        }
        return {
            entranceUrls,
            elevatorUrls,
            hasMultipleFloors,
            durationMillisImageUpload: Date.now() - startImageUpload,
        };
    });
}
function submitRegistration(api, queryClient, placeId, values, selectedOption, selectedStandaloneType, selectedFloor, doorDirection, uploaded, requestId) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e;
        const startTotal = Date.now();
        const imageCount = ((_b = (_a = values.entrancePhotos) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) + ((_d = (_c = values.elevatorPhotos) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0);
        const { entranceUrls, elevatorUrls, hasMultipleFloors, durationMillisImageUpload, } = uploaded;
        let floorMovingElevatorAccessibility;
        if (elevatorUrls) {
            floorMovingElevatorAccessibility = {
                imageUrls: elevatorUrls,
                stairInfo: typeof values.elevatorHasStairs === 'boolean'
                    ? values.elevatorHasStairs
                        ? values.elevatorStairInfo
                        : StairInfo.None
                    : undefined,
                stairHeightLevel: values.elevatorHasStairs && values.elevatorStairInfo === StairInfo.One
                    ? values.elevatorStairHeightLevel
                    : undefined,
                hasSlope: typeof values.elevatorHasSlope === 'boolean'
                    ? values.elevatorHasSlope
                    : undefined,
            };
        }
        const requestData = {
            placeId,
            isStandaloneBuilding: selectedOption === 'standalone',
            doorDirectionType: selectedOption === 'standalone'
                ? PlaceDoorDirectionTypeDto.OutsideBuilding
                : doorDirection === 'outside'
                    ? PlaceDoorDirectionTypeDto.OutsideBuilding
                    : PlaceDoorDirectionTypeDto.InsideBuilding,
            floors: computeFloors(selectedOption, selectedFloor, selectedStandaloneType),
            imageUrls: entranceUrls,
            stairInfo: values.hasStairs ? values.stairInfo : StairInfo.None,
            stairHeightLevel: values.hasStairs && values.stairInfo === StairInfo.One
                ? values.entranceStairHeightLevel
                : undefined,
            hasSlope: values.hasSlope || false,
            entranceDoorTypes: values.doorType,
            features: values.additionalInfo,
            entranceComment: values.comment,
            floorMovingMethodTypes: hasMultipleFloors
                ? values.floorMovementMethod
                : undefined,
            floorMovingMethodComment: hasMultipleFloors
                ? values.floorMovementComment
                : undefined,
            floorMovingElevatorAccessibility,
        };
        try {
            const startApiCall = Date.now();
            const res = yield api.registerPlaceAccessibilityV2Post(requestData, requestId, { timeout: REGISTER_TIMEOUT_MS });
            const durationApiCall = Date.now() - startApiCall;
            yield Logger.logAccessibilityRegistration({
                type: 'place',
                durationMillisImageUpload,
                durationMillisApiCall: durationApiCall,
                durationMillisTotal: durationMillisImageUpload + (Date.now() - startTotal),
                imageCount,
                success: true,
            });
            queryClient.invalidateQueries({
                queryKey: ['PlaceDetailV2', placeId, 'Accessibility'],
            });
            queryClient.invalidateQueries({
                queryKey: ['PlaceDetailV2', placeId],
            });
            // 정복(챌린지 기여)으로 바뀐 남은 매장 목록·챌린지 진척도·홈 카드 갱신.
            invalidateChallengeQueries(queryClient);
            updateSearchCacheForPlaceAsync(api, queryClient, placeId);
            return {
                success: true,
                data: (_e = res.data.contributedChallengeInfos) === null || _e === void 0 ? void 0 : _e.flatMap(info => info.completedQuestsByContribution.map(quest => ({
                    challengeId: info.challenge.id,
                    type: quest.completeStampType,
                    title: quest.title,
                }))),
                // ctpl 정복 완료 축하(getCtplConquest)가 필요로 하는 원본 배열. 위 data는
                // 이미 퀘스트 완료 스탬프용으로 flatMap된 형태라 challenge.goal 등을 잃는다.
                contributedChallengeInfos: res.data.contributedChallengeInfos,
                // 축하를 첫 정복에만 띄우기 위한 서버 판정. 서버가 PA insert 전에 읽은 값이다.
                isFirstPlaceAccessibility: res.data.isFirstPlaceAccessibility,
            };
        }
        catch (error) {
            yield Logger.logAccessibilityRegistration({
                type: 'place',
                durationMillisImageUpload,
                durationMillisApiCall: Date.now() - startTotal,
                durationMillisTotal: durationMillisImageUpload + (Date.now() - startTotal),
                imageCount,
                success: false,
            });
            ToastUtils.showOnApiError(error);
            return {
                success: false,
            };
        }
    });
}
function getEvent(selectedOption, doorDirection) {
    if (selectedOption === 'standalone') {
        // 단독건물이면 event 없음
        return undefined;
    }
    else {
        // 단독건물이 아닌 경우
        // 1. 1층 + 건물 밖 문 => event 없음
        // 2. 다른층 + 건물 밖 문 => registration-suggest
        // 3. 그 외 => registration-force
        const isFirstFloorWithOutsideDoor = selectedOption === 'firstFloor' && doorDirection === 'outside';
        const isOtherFloorWithOutsideDoor = selectedOption === 'otherFloor' && doorDirection === 'outside';
        if (isFirstFloorWithOutsideDoor) {
            return undefined;
        }
        else if (isOtherFloorWithOutsideDoor) {
            return 'registration-suggest';
        }
        else {
            return 'registration-force';
        }
    }
}
